import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ProductGallery.css';

/**
 * ProductGallery Component
 * - Automatically advances photos with a smooth timeline (progress bar)
 * - Pauses on mouse hover and RESUMES from the EXACT point where it paused
 * - Enlarged, high-impact presentation
 * - Interactive thumbnails with active indicator
 * - Next / Prev controls
 * - Fullscreen lightbox preview modal
 */
const ProductGallery = ({
  images = [],
  productName = 'Producto',
  sku = '',
  autoPlayInterval = 3500,
  onImageChange = null
}) => {
  // Filter out null / empty image urls
  const validImages = images.filter(img => typeof img === 'string' && img.trim().length > 0);
  const fallbackImage = '/img/placeholder-product.png';
  const displayImages = validImages.length > 0 ? validImages : [fallbackImage];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  // Smooth timeline progress state (0 to 100%)
  const [progress, setProgress] = useState(0);
  const elapsedRef = useRef(0);
  const lastTimestampRef = useRef(null);
  const animFrameRef = useRef(null);

  const resetProgress = useCallback(() => {
    elapsedRef.current = 0;
    setProgress(0);
  }, []);

  const goToNext = useCallback(() => {
    resetProgress();
    setCurrentIndex((prev) => {
      const nextIndex = (prev + 1) % displayImages.length;
      if (onImageChange) onImageChange(displayImages[nextIndex], nextIndex);
      return nextIndex;
    });
  }, [displayImages, onImageChange, resetProgress]);

  const goToPrev = useCallback(() => {
    resetProgress();
    setCurrentIndex((prev) => {
      const nextIndex = (prev - 1 + displayImages.length) % displayImages.length;
      if (onImageChange) onImageChange(displayImages[nextIndex], nextIndex);
      return nextIndex;
    });
  }, [displayImages, onImageChange, resetProgress]);

  const goToIndex = (index) => {
    resetProgress();
    setCurrentIndex(index);
    if (onImageChange) onImageChange(displayImages[index], index);
  };

  // Continuous animation loop via requestAnimationFrame
  // Pauses at the exact millisecond on hover, and RESUMES from that exact point when unhovered!
  useEffect(() => {
    if (displayImages.length <= 1) {
      setProgress(0);
      return;
    }

    lastTimestampRef.current = performance.now();

    const loop = (now) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = now;
      }
      const delta = now - lastTimestampRef.current;
      lastTimestampRef.current = now;

      if (!isHovered && isAutoPlayEnabled) {
        elapsedRef.current += delta;
        const pct = Math.min(100, (elapsedRef.current / autoPlayInterval) * 100);
        setProgress(pct);

        if (elapsedRef.current >= autoPlayInterval) {
          elapsedRef.current = 0;
          setProgress(0);
          goToNext();
        }
      }

      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      lastTimestampRef.current = null;
    };
  }, [displayImages.length, isHovered, isAutoPlayEnabled, autoPlayInterval, goToNext]);

  // Touch swipe support for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) {
      goToNext();
    } else if (diff < -50) {
      goToPrev();
    }
    setTouchStart(null);
  };

  return (
    <div className="product-gallery-root">
      {/* Main Large Image Container */}
      <div
        className="product-gallery-main-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Status Badges */}
        <div className="product-gallery-badges">
          {displayImages.length > 1 && (
            <span className="gallery-badge-counter">
              {currentIndex + 1} / {displayImages.length}
            </span>
          )}
          {displayImages.length > 1 && (
            <button
              type="button"
              className={`gallery-autoplay-toggle ${isHovered ? 'is-paused' : ''}`}
              onClick={() => setIsAutoPlayEnabled(!isAutoPlayEnabled)}
              title={isAutoPlayEnabled ? 'Pausar rotación automática' : 'Reanudar rotación automática'}
            >
              <span className="material-symbols-outlined text-sm">
                {isAutoPlayEnabled && !isHovered ? 'motion_photos_on' : 'motion_photos_paused'}
              </span>
              <span className="text-xs hidden sm:inline">
                {isHovered ? 'Pausado' : isAutoPlayEnabled ? 'Auto' : 'Pausa'}
              </span>
            </button>
          )}
        </div>

        {/* Main Image with Zoom Click */}
        <div
          className="product-gallery-image-wrapper"
          onClick={() => setLightboxOpen(true)}
          title="Haz clic para ver en tamaño completo"
        >
          <img
            key={currentIndex}
            src={displayImages[currentIndex]}
            alt={`${productName} - Vista ${currentIndex + 1}`}
            className="product-gallery-main-img"
            loading="eager"
          />
          <div className="product-gallery-zoom-hint">
            <span className="material-symbols-outlined">zoom_in</span>
            <span>Ver pantalla completa</span>
          </div>
        </div>

        {/* Arrow Navigation (if more than 1 image) */}
        {displayImages.length > 1 && (
          <>
            <button
              type="button"
              className="gallery-nav-arrow arrow-prev"
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              aria-label="Foto anterior"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <button
              type="button"
              className="gallery-nav-arrow arrow-next"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Siguiente foto"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </>
        )}

        {/* Dynamic Progress Line - Preserves and resumes exact position upon hover/unhover */}
        {displayImages.length > 1 && isAutoPlayEnabled && (
          <div className={`gallery-progress-bar ${isHovered ? 'progress-paused' : ''}`}>
            <div
              className="gallery-progress-inner"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Thumbnails Row */}
      {displayImages.length > 1 && (
        <div className="product-gallery-thumbnails">
          {displayImages.map((img, idx) => (
            <button
              key={idx}
              type="button"
              className={`product-gallery-thumb ${idx === currentIndex ? 'active' : ''}`}
              onClick={() => goToIndex(idx)}
              onMouseEnter={() => {
                goToIndex(idx);
                setIsHovered(true);
              }}
              aria-label={`Ver foto ${idx + 1}`}
            >
              <img src={img} alt={`${productName} miniatura ${idx + 1}`} />
              {idx === currentIndex && <span className="thumb-active-indicator" />}
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Lightbox Modal */}
      {lightboxOpen && (
        <div className="gallery-lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <div className="gallery-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="gallery-lightbox-close"
              onClick={() => setLightboxOpen(false)}
              aria-label="Cerrar modal"
            >
              <span className="material-symbols-outlined">close</span>
            </button>

            <img
              src={displayImages[currentIndex]}
              alt={`${productName} en grande`}
              className="gallery-lightbox-img"
            />

            <div className="gallery-lightbox-footer">
              <span className="text-sm text-gray-300">
                {productName} {sku && `(SKU: ${sku})`} • Foto {currentIndex + 1} de {displayImages.length}
              </span>
              {displayImages.length > 1 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost text-white"
                    onClick={goToPrev}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary text-black font-semibold"
                    onClick={goToNext}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
